import { AGENT_STATUS, AgentKeys } from '@ag-common-lib/public-api';
import { inject, Pipe, PipeTransform } from '@angular/core';
import { AgentChangesService, AgentSearchService } from 'ag-common-svc/public-api';
import { from, map, of, startWith, switchMap } from 'rxjs';

@Pipe({ name: 'isShowAgentNotWelcomedWarning' })
export class IsShowAgentNotWelcomedWarningPipe implements PipeTransform {
  private readonly agentSearchService = inject(AgentSearchService);
  private readonly agentChangesService = inject(AgentChangesService);

  /**
   * One agent per rendered row, rather than an index into a map of every agent.
   *
   * `getById` caches per dbId and the projection's change signal evicts exactly the
   * entries it names, so a grid of repeated owners costs one request each and a status
   * edited elsewhere still repaints — the signal is what re-runs this, since a pure pipe
   * would otherwise never look again.
   */
  transform(agentsId: string) {
    // The one host binds this as `!agentDbId || agentDbId | ...`, and the pipe binds
    // looser than `||`, so a request with no agent arrives here as boolean `true`.
    // Warn, rather than asking the API for an agent whose id is not an id.
    if (!agentsId || typeof agentsId !== 'string') {
      return of(true);
    }

    return this.agentChangesService.agentsChanged$.pipe(
      startWith(undefined),
      switchMap(() => from(this.agentSearchService.getById(agentsId))),
      map(agent => {
        if (!agent) {
          return true;
        }

        const agentStatus = agent?.[AgentKeys.agent_status];
        const isAgentNotWelcomed = new Set([AGENT_STATUS.INACTIVE, AGENT_STATUS.DENIED]).has(agentStatus);

        return isAgentNotWelcomed;
      }),
    );
  }
}
